import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { Link, useParams } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { categoryComics, hotComics } from '@/mocks/comics';

const categorySlugMap: Record<string, string> = {
  romance: 'romance',
  fantasy: 'fantasy',
  action: 'action',
  suspense: 'suspense',
  comedy: 'comedy',
  healing: 'healing',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const categoryKey = slug ? categorySlugMap[slug] : null;
  const category = categories.find((c) => c.slug === categoryKey);
  const comics = categoryKey && categoryComics[categoryKey] ? categoryComics[categoryKey] : hotComics;

  if (!category) {
    return (
      <>
        <SEOHead title="分类未找到 - 基德漫画网" description="抱歉，未找到该分类" />
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="font-heading text-2xl text-foreground-900 mb-3">分类未找到</h1>
            <p className="text-foreground-500 mb-6">请检查分类名称是否正确</p>
            <Link to="/" className="px-6 py-2 bg-primary-500 text-white rounded-full text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`${category.name}漫画 - 韩漫免费漫画在线观看下拉式 - 基德漫画网`}
        description={`基德漫画网${category.name}漫画专区，提供${category.count}部${category.name}类韩漫免费漫画在线观看下拉式阅读。${category.description}`}
        keywords={`${category.name}漫画,韩漫${category.name},漫画免费漫画,在线观看下拉式,基德漫画网`}
        canonicalUrl={`http://bjgjfh.com/category/${category.slug}`}
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name}漫画 - 基德漫画网`,
            description: `${category.name}类韩漫免费漫画专区`,
          },
        ]}
      />
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: `${category.name}漫画` },
              ]}
            />
          </div>
        </div>

        <section className="relative w-full h-[320px] md:h-[420px] overflow-hidden">
          <img
            src={category.image}
            alt={`${category.name}漫画 - 韩漫免费漫画在线观看下拉式`}
            title={`${category.name}漫画 - 好看韩漫漫画免费漫画`}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/25 to-black/60"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <h1 className="font-heading text-3xl md:text-5xl text-white mb-3">
                {category.name}
                <strong className="font-heading text-accent-400">漫画</strong>
              </h1>
              <p className="text-sm md:text-base text-white/75 max-w-lg mx-auto">
                {category.description} · 共 {category.count} 部作品
              </p>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
              {comics.map((comic, index) => (
                <ComicCard
                  key={comic.id}
                  id={comic.id}
                  title={comic.title}
                  author={comic.author}
                  coverImage={comic.coverImage}
                  category={comic.category}
                  rating={comic.rating}
                  views={comic.views}
                  tags={comic.tags}
                  isFirst={index === 0}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl md:text-3xl text-foreground-950 mb-8 text-center">
              跨界推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categories.filter(c => c.slug !== categoryKey).slice(0, 3).map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group relative overflow-hidden rounded-lg aspect-[4/3] cursor-pointer"
                >
                  <img
                    src={cat.image}
                    alt={`${cat.name}漫画推荐`}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <h3 className="font-heading text-xl md:text-2xl text-white drop-shadow-lg">
                      {cat.name}
                    </h3>
                    <span className="mt-2 px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs text-white whitespace-nowrap">
                      {cat.count} 部作品
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}